import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Form, Input, InputNumber, List, Row, Select, Space, Switch, Tag, Typography, message } from 'antd';
import API_CONFIG from '../../config/api.js';
import { getAuthorizedCompanyContext } from '../../utils/companyContext.js';

const { Text } = Typography;

interface Promotion {
  id: number;
  code: string;
  discount_type: 'percent' | 'fixed';
  discount_value: number;
  starts_at: string | null;
  ends_at: string | null;
  max_uses: number | null;
  used_count: number;
  is_active: boolean | number;
}

interface PromotionForm {
  code: string;
  discount_type: 'percent' | 'fixed';
  discount_value: number;
  starts_at?: { toISOString(): string };
  ends_at?: { toISOString(): string };
  max_uses?: number;
}

const promotionUrl = (storeId: number) => `${API_CONFIG.BASE_URL}/api/ecommerce/stores/${storeId}/promotions`;
const authHeaders = () => {
  const companyId = getAuthorizedCompanyContext();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    ...(companyId ? { 'X-Company-Id': String(companyId) } : {}),
  };
};

const StorePromotions: React.FC<{ storeId: number; canEdit: boolean }> = ({ storeId, canEdit }) => {
  const [form] = Form.useForm<PromotionForm>();
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const response = await fetch(promotionUrl(storeId), { headers: authHeaders(), signal });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Codes promotionnels indisponibles.');
      if (!signal?.aborted) setPromotions(data.promotions || []);
    } catch (error) {
      if (!signal?.aborted) {
        setPromotions([]);
        message.error(error instanceof Error ? error.message : 'Codes promotionnels indisponibles.');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    const controller = new AbortController();
    setPromotions([]);
    setFormOpen(false);
    form.resetFields();
    void load(controller.signal);
    return () => controller.abort();
  }, [storeId, load, form]);

  const create = async (values: PromotionForm) => {
    setSaving(true);
    try {
      const response = await fetch(promotionUrl(storeId), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          code: values.code.trim().toUpperCase(),
          discount_type: values.discount_type,
          discount_value: values.discount_value,
          starts_at: values.starts_at?.toISOString() || null,
          ends_at: values.ends_at?.toISOString() || null,
          max_uses: values.max_uses ?? null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Création impossible.');
      message.success(`Code ${data.code} créé.`);
      form.resetFields();
      setFormOpen(false);
      await load();
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Création impossible.');
    } finally { setSaving(false); }
  };

  const toggle = async (promotion: Promotion, active: boolean) => {
    setSaving(true);
    try {
      const response = await fetch(`${promotionUrl(storeId)}/${promotion.id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ is_active: active }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Mise à jour impossible.');
      await load();
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Mise à jour impossible.');
    } finally { setSaving(false); }
  };

  const discountType = Form.useWatch('discount_type', form);

  return <Space direction="vertical" size="middle" style={{ width: '100%' }}>
    <Alert type="info" showIcon message="Un code par commande" description="La réduction porte sur les articles, hors livraison. Les codes expirés, désactivés ou arrivés à leur limite sont refusés au paiement." />
    {canEdit && <Button type="primary" onClick={() => setFormOpen((open) => !open)}>
      {formOpen ? 'Fermer le formulaire' : 'Créer un code'}
    </Button>}
    {canEdit && formOpen && <Card size="small" title="Nouveau code promotionnel">
      <Form form={form} layout="vertical" initialValues={{ discount_type: 'percent' }} onFinish={(values) => void create(values)}>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Saisissez un code.' }, { pattern: /^[A-Za-z0-9][A-Za-z0-9-]{2,39}$/, message: '3 à 40 lettres, chiffres ou tirets.' }]}>
              <Input maxLength={40} placeholder="PROMO10" autoComplete="off" />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name="discount_type" label="Type" rules={[{ required: true }]}>
              <Select options={[{ value: 'percent', label: 'Pourcentage' }, { value: 'fixed', label: 'Montant fixe' }]} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name="discount_value" label={discountType === 'fixed' ? 'Montant FCFA' : 'Pourcentage'} rules={[{ required: true, message: 'Indiquez la réduction.' }]}>
              <InputNumber style={{ width: '100%' }} min={0.01} max={discountType === 'fixed' ? 99999999.99 : 100} precision={2} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="starts_at" label="Début (facultatif)"><DatePicker showTime style={{ width: '100%' }} format="DD/MM/YYYY HH:mm" /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="ends_at" label="Fin (facultatif)"><DatePicker showTime style={{ width: '100%' }} format="DD/MM/YYYY HH:mm" /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="max_uses" label="Nombre maximal de commandes (facultatif)"><InputNumber style={{ width: '100%' }} min={1} max={1000000000} precision={0} /></Form.Item>
          </Col>
        </Row>
        <Button type="primary" htmlType="submit" loading={saving}>Enregistrer le code</Button>
      </Form>
    </Card>}
    <List
      loading={loading}
      locale={{ emptyText: 'Aucun code pour cette boutique.' }}
      dataSource={promotions}
      renderItem={(promotion) => <List.Item actions={canEdit ? [<Switch key="active" checked={Boolean(Number(promotion.is_active))} loading={saving} checkedChildren="Actif" unCheckedChildren="Inactif" onChange={(active) => void toggle(promotion, active)} />] : []}>
        <List.Item.Meta
          title={<Space wrap><Text code>{promotion.code}</Text><Tag>{promotion.discount_type === 'percent' ? `${promotion.discount_value} %` : `${Number(promotion.discount_value).toLocaleString('fr-FR')} FCFA`}</Tag></Space>}
          description={<Space size="small" wrap>
            <Text type="secondary">{Number(promotion.used_count)} / {promotion.max_uses ?? '∞'} utilisation(s)</Text>
            {promotion.starts_at && <Text type="secondary">Du {new Date(promotion.starts_at).toLocaleDateString('fr-FR')}</Text>}
            {promotion.ends_at && <Text type="secondary">au {new Date(promotion.ends_at).toLocaleDateString('fr-FR')}</Text>}
          </Space>}
        />
      </List.Item>}
    />
  </Space>;
};

export default StorePromotions;
